import { Cell, Pagination, Row, SortableHeader, Table, Top } from 'components/Table';
import { isMobile } from 'react-device-detect';
import {
  ACTIVITIES_TYPE,
  ENTITIES_COLUMNS,
  LABS_TYPE,
  QUIZZES_TYPE,
  STATISTICS_ENTITIES,
  USERS_TYPE,
  VIDEOS_OVERALL_TYPE,
  VIDEOS_TYPE,
} from 'constants/analytics';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AnalyticsContext } from 'contexts/analytics';
import { SessionContext } from 'contexts/session';
import { getColumnDirection, getNextSortState, toOrdering } from 'utils/sorting';
import { UNSORTED } from 'types/sorting';
import {
  EmptyTable,
  EntitiesWrapper,
  EntityButton,
  OptionsSection,
  Separator,
  TableContainer,
  TableLabel,
  TableWrapper,
} from './styles';
import './statics.scss';
import { Spinner } from 'components/loadings';
import Icon from 'components/icons';
import Notification from 'components/notification';
import ExportButton from '../ExportButton';
import RowMobile from './RowMobile';
import TableCellContent from './TableCellContent';
import UnderConstructionTable from './UnderConstructionTable';
import { Tooltip } from 'antd';
import { IColumnMetadata } from 'contexts/analytics/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  TableCsvExportFailureReason,
  downloadCsvBlob,
  getExportType,
  runTableCsvExport,
} from 'utils/tableCsvExport';

const iconStyles = {
  marginLeft: '5px',
  width: '14px',
  height: '14px',
} as React.CSSProperties;

const TABLE_EXPORT_ERROR_MESSAGES: Record<TableCsvExportFailureReason, string> = {
  permission_denied: "You don't have permission to export this data.",
  request_failed: 'Invalid export request. Please refresh and try again.',
  no_data: 'No data to export. Adjust your filters and try again.',
  row_limit_exceeded:
    'Export exceeds 50,000 rows. Narrow your filters or date range and try again.',
  file_size_exceeded: 'Export exceeds 25 MB. Narrow your filters or date range and try again.',
  upstream_analytics_failed: 'Export failed. Please try again.',
  unexpected_response_shape: 'Export failed unexpectedly. Please try again.',
  timeout: 'Export timed out. Narrow your filters or date range and try again.',
};

type Props = {
  section?: string;
  hideHeader?: boolean;
};

const EnititesTable = ({ section, hideHeader }: Props): JSX.Element => {
  const { actions, currentChart, entitiesTable, tableOrdering, filters } =
    useContext(AnalyticsContext);
  const { user, account, isAccountOwner } = useContext(SessionContext);
  const { dashboardAnalyticsSorting, dashboardAnalyticsExports } = useFlags() || {};
  const sortState = dashboardAnalyticsSorting ? tableOrdering ?? UNSORTED : UNSORTED;
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!dashboardAnalyticsSorting && tableOrdering?.columnKey) {
      actions.onChangeOrdering(UNSORTED);
    }
  }, [dashboardAnalyticsSorting]);

  const activeUsersColumnsTableConfig = useMemo(() => {
    if (isMobile) return '90% 1fr';
    switch (currentChart) {
      case VIDEOS_TYPE.VALUE.toLowerCase():
      case VIDEOS_OVERALL_TYPE.VALUE.toLowerCase():
        return '2fr repeat(4, 1fr)';
      case QUIZZES_TYPE.VALUE.toLowerCase():
      case LABS_TYPE.VALUE.toLowerCase():
        return '2fr repeat(3, 0.7fr) repeat(2, 1.2fr)';
      case USERS_TYPE.VALUE.toLowerCase():
        return '1.8fr 0.6fr repeat(2, 0.8fr)  2.5fr';
      case ACTIVITIES_TYPE.VALUE.toLowerCase():
        return '1.5fr repeat(4, 1fr)';
      default:
        return '1.5fr repeat(3, 1fr)';
    }
  }, [currentChart]);

  const columnsMetadata: IColumnMetadata[] =
    ENTITIES_COLUMNS[currentChart.toUpperCase()]?.[entitiesTable.currentEntity] ?? [];

  const tableTabs = STATISTICS_ENTITIES[currentChart.toUpperCase()] ?? STATISTICS_ENTITIES.DEFAULT;

  const updateSelectedEntity = (value: string) => {
    actions && actions.onUpdateEntity(value);
  };
  const isEntitySelected = (entity: string) => {
    return entitiesTable.currentEntity === entity;
  };
  const isEntityUnavailable = () => {
    return entitiesTable.unavailable;
  };
  const showBadge = (value) => (value === '100%' && !isMobile ? 'var(--positive-light)' : '');

  const handleSortToggle = (columnKey: string) => {
    if (!actions || !dashboardAnalyticsSorting) return;
    const nextState = getNextSortState(sortState, columnKey);
    actions.onChangeOrdering(nextState);
  };

  const exportType = getExportType(currentChart, entitiesTable.currentEntity);

  const handleTableCsvExport = async () => {
    if (isExporting || !exportType) return;
    if (!entitiesTable.items?.length) {
      Notification({ type: 'error', text: TABLE_EXPORT_ERROR_MESSAGES.no_data });
      return;
    }
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const columnKeyMap = columnsMetadata.reduce<Record<string, string>>((acc, col) => {
        if (col.sortable && col.sortKey) acc[col.name] = col.sortKey;
        return acc;
      }, {});

      const fallbackTeamIds = !isAccountOwner
        ? (user.teams ?? [])
            .filter((team) => team.is_manager)
            .map((team) => team.team_id)
            .join(',')
        : '';

      const params: Record<string, unknown> = {
        account_id: account?.id,
        user_id: user.uaa_id,
        date_from: filters.date_from,
        date_to: filters.date_to,
        period: filters.period,
        team_ids: filters.team_ids || fallbackTeamIds,
        ordering: toOrdering(sortState, columnKeyMap),
      };
      if (currentChart === USERS_TYPE.VALUE.toLowerCase()) {
        params.filter_licensed_users = true;
      }

      const result = await runTableCsvExport(exportType, params);
      if (result.ok === false) {
        Notification({ type: 'error', text: TABLE_EXPORT_ERROR_MESSAGES[result.reason] });
        return;
      }
      downloadCsvBlob(result.blob, result.filename);
    } finally {
      setIsExporting(false);
    }
  };

  const showHeaderSection = !hideHeader || dashboardAnalyticsExports;

  return (
    <TableWrapper loading={entitiesTable.loading}>
      <Spinner
        active={entitiesTable.loading}
        top={isMobile ? '170px' : '270px'}
        position="relative"
      />
      <Separator compact={!showHeaderSection} />
      {showHeaderSection && (
        <OptionsSection lock={entitiesTable.loading}>
          {!hideHeader && <TableLabel>Activity</TableLabel>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
            {!hideHeader && (
              <EntitiesWrapper>
                {tableTabs?.map(({ LABEL: label, VALUE: value }) => {
                  const isSelected = isEntitySelected(value);
                  return (
                    <EntityButton
                      active={isSelected}
                      key={`entity-${value}`}
                      onClick={() => updateSelectedEntity(value)}
                    >
                      <span>{label.toUpperCase()}</span>
                    </EntityButton>
                  );
                })}
              </EntitiesWrapper>
            )}
            {dashboardAnalyticsExports && (
              <ExportButton
                label="Export table"
                loadingLabel="Exporting table"
                data-testid="export-table-button"
                disabled={entitiesTable.loading || !exportType}
                isLoading={isExporting}
                onExport={handleTableCsvExport}
              />
            )}
          </div>
        </OptionsSection>
      )}
      <TableContainer lock={entitiesTable.loading}>
        {(isEntityUnavailable() && (
          <UnderConstructionTable entity={entitiesTable.currentEntity} />
        )) || (
          <>
            {entitiesTable?.items?.length !== 0 ? (
              <Table className={section ? `${section}-table` : ''}>
                <Row
                  className="entities-table__table-header"
                  style={{ padding: hideHeader ? '24px 0 15px' : '40px 0 15px' }}
                  columns={activeUsersColumnsTableConfig}
                  top={true}
                >
                  {isMobile ? (
                    <Top>{columnsMetadata[0]?.label.toUpperCase()}</Top>
                  ) : (
                    columnsMetadata?.map((column, index) => (
                      <Top key={index} className={column.name}>
                        {column.sortable && dashboardAnalyticsSorting ? (
                          <SortableHeader
                            label={column.label}
                            columnKey={column.name}
                            direction={getColumnDirection(sortState, column.name)}
                            onToggle={handleSortToggle}
                            disabled={entitiesTable.loading}
                            tooltip={column.tooltip}
                          />
                        ) : (
                          <>
                            {column.label}
                            {column?.tooltip && (
                              <Tooltip
                                title={column?.tooltip}
                                placement="right"
                                overlayInnerStyle={{ textAlign: 'center' }}
                              >
                                <Icon
                                  data-testid="info-inactive-icon"
                                  style={iconStyles}
                                  icon="Info"
                                  fill={'var(--lilac)'}
                                  size="small"
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Top>
                    ))
                  )}
                </Row>
                {entitiesTable?.items?.map((item, index) => (
                  <Row columns={activeUsersColumnsTableConfig} key={index}>
                    {columnsMetadata?.map((column, index) => (
                      <Cell
                        className={column.name}
                        key={index}
                        badge={showBadge(column.value(item))}
                      >
                        <TableCellContent
                          key={index}
                          column={column}
                          index={index}
                          entity={entitiesTable.currentEntity}
                          item={item}
                          showValues={!isMobile}
                          card={false}
                          isLoading={entitiesTable.loading}
                        />
                      </Cell>
                    ))}
                    <Cell hidden={!isMobile} className="entities-table__table-mobile-details">
                      <RowMobile
                        data={item}
                        metadata={columnsMetadata}
                        entity={entitiesTable.currentEntity}
                      ></RowMobile>
                    </Cell>
                  </Row>
                ))}
              </Table>
            ) : (
              <EmptyTable>
                <div className="entities-table__empty_title">No data available</div>
                <div className="entities-table__empty_subtitle">
                  No {entitiesTable.currentEntity} data available yet
                </div>
              </EmptyTable>
            )}
            {entitiesTable.items && entitiesTable.items.length != 0 && (
              <Pagination
                count={entitiesTable.count}
                page={entitiesTable.page}
                page_size={entitiesTable.pageSize}
                onPagesClick={(value, name) => actions.onChangePagination({ name: name, value })}
              />
            )}
          </>
        )}
      </TableContainer>
    </TableWrapper>
  );
};

export default EnititesTable;
